/**
 * PayPal subscription server functions used by the public checkout flow
 * and the return/verify pages.
 *
 * Every function that touches PayPal or the DB is a createServerFn — never
 * import this into browser code without going through useServerFn.
 *
 * Access model:
 *  - createFoundingPaypalSubscription: signed-in user only (requireSupabaseAuth)
 *  - getMyPaypalSubscription:          signed-in user only
 *  - getPaypalCheckoutVisibility:      signed-in user; returns whether the
 *    PayPal button should render (admin OR feature flag on).
 */

import { createServerFn } from "@tanstack/react-start";
import { checkoutAttributionSchema } from "./checkout-attribution";
import { recordCheckoutAttribution } from "./checkout-attribution.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { digitalConsentSchema, MISSING_CONSENT_MESSAGE } from "./digital-consent";
import { recordDigitalConsent } from "./digital-consent.server";
import {
  createPaypalSubscription,
  getPaypalSubscription,
  type PaypalSubscription,
} from "./paypal.server";

/**
 * Apply a Recovery Trauma™ voucher code to a PayPal checkout.
 *
 * PayPal cannot take an ad-hoc discount on an existing plan, so a voucher is
 * mirrored as its own PayPal billing plan under the same product: a
 * discounted opening cycle, then the normal price. The member approves the
 * discounted amount inside PayPal itself, so what they see is what they pay.
 *
 * Returns the plan to send the member to. If anything about the code no
 * longer holds up, we quietly return the normal plan — a bad code never
 * blocks someone from joining.
 */
async function applyPaypalVoucher(
  admin: unknown,
  opts: {
    code?: string | null;
    userId: string;
    email: string | null;
    membershipPlanId: string;
    productId: string;
    fallbackPlanId: string;
  },
): Promise<{ planId: string; resolved: unknown | null }> {
  if (!opts.code) return { planId: opts.fallbackPlanId, resolved: null };
  try {
    const { resolveVoucher, ensurePaypalVoucherPlan } = await import("./vouchers.server");
    const result = await resolveVoucher(admin as never, opts.code, {
      planId: opts.membershipPlanId,
      userId: opts.userId,
    });
    if (!result.ok) return { planId: opts.fallbackPlanId, resolved: null };
    const planId = await ensurePaypalVoucherPlan(
      admin as never,
      result.resolved,
      opts.productId,
    );
    return { planId, resolved: result.resolved };
  } catch (err) {
    console.error("[paypal] voucher plan failed, continuing at full price", err);
    return { planId: opts.fallbackPlanId, resolved: null };
  }
}

/** Reserve the code against this PayPal subscription; released if never paid. */
async function holdPaypalVoucher(
  admin: unknown,
  resolved: unknown | null,
  opts: { userId: string; email: string | null; subscriptionId: string },
): Promise<void> {
  if (!resolved) return;
  const { placeVoucherHold } = await import("./vouchers.server");
  await placeVoucherHold(admin as never, resolved as never, {
    userId: opts.userId,
    email: opts.email,
    provider: "paypal",
    reference: opts.subscriptionId,
  });
}

// Launch anchor: 1 August 2026 00:00 UTC. Retained for reference only.
const LAUNCH_AT_UTC = Date.UTC(2026, 7, 1, 0, 0, 0);
void LAUNCH_AT_UTC;

/**
 * Decide the PayPal subscription `start_time` server-side.
 *
 * The live plan (P-0AE27420N4044591UNJV54AQ) is a straight $14.99 USD/month
 * subscription with no setup fee, matching Stripe. Billing therefore starts
 * immediately at approval — returning null means "start now". The old
 * pre-launch deferral (setup fee now, recurring from 1 Sept 2026) belonged to
 * the retired $14.99 founding plan and would otherwise hand new members a
 * free month.
 */
export function computePaypalStartTime(_nowMs = Date.now()): string | null {
  return null;
}


// ---------- Visibility ----------

/**
 * Email allow-list gate for PayPal checkout during the private test window.
 * PAYPAL_TEST_ALLOWLIST is a comma-separated list of emails (case-insensitive).
 * When the `paypal_public_checkout_enabled` feature flag is turned on later,
 * everyone sees PayPal regardless of the allow-list.
 */
function isPaypalAllowedEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const raw = process.env.PAYPAL_TEST_ALLOWLIST ?? "";
  if (!raw.trim()) return false;
  const list = raw
    .split(/[,\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return list.includes(email.trim().toLowerCase());
}

async function readPaypalGateState(
  supabase: import("@supabase/supabase-js").SupabaseClient,
  email: string | null | undefined,
) {
  const { data } = await supabase
    .from("feature_flags")
    .select("enabled")
    .eq("key", "paypal_public_checkout_enabled")
    .maybeSingle();
  const publicOn = !!data?.enabled;
  const allowlisted = isPaypalAllowedEmail(email);
  return { publicOn, allowlisted, visible: publicOn || allowlisted };
}

async function resolveUserEmail(
  supabase: import("@supabase/supabase-js").SupabaseClient,
  claims: Record<string, unknown> | undefined,
): Promise<string | null> {
  const claimEmail = typeof claims?.email === "string" ? (claims.email as string) : null;
  if (claimEmail) return claimEmail;
  try {
    const { data } = await supabase.auth.getUser();
    return data?.user?.email ?? null;
  } catch {
    return null;
  }
}

export const getPaypalCheckoutVisibility = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const email = await resolveUserEmail(
      context.supabase,
      context.claims as Record<string, unknown> | undefined,
    );
    const state = await readPaypalGateState(context.supabase, email);
    return { visible: state.visible, allowlisted: state.allowlisted, publicOn: state.publicOn };
  });

// ---------- Create subscription ----------

export const createFoundingPaypalSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({
        return_url: z.string().url(),
        cancel_url: z.string().url(),
        /** Membership plan chosen before account creation (monthly/6-month/yearly). */
        plan_id: z.string().trim().max(60).optional(),
        /** Optional Recovery Trauma™ voucher code, re-validated server-side. */
        voucher_code: z.string().trim().max(60).nullish().transform((v) => v ?? undefined),
        attribution: checkoutAttributionSchema,
        digital_consent: digitalConsentSchema,
      })
      .parse(data),
  )

  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (!data.digital_consent) {
      return { ok: false as const, reason: "consent_required" as const, message: MISSING_CONSENT_MESSAGE };
    }
    const email = await resolveUserEmail(supabase, context.claims as Record<string, unknown> | undefined);

    // Visibility gate — must be allow-listed OR public flag on.
    const gate = await readPaypalGateState(supabase, email);
    if (!gate.visible) {
      return { ok: false as const, reason: "unavailable" as const, message: "PayPal checkout is not currently enabled." };
    }


    // Block duplicate active memberships (Stripe or PayPal).
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [stripeMem, existingPaypal] = await Promise.all([
      supabaseAdmin
        .from("memberships")
        .select("status, paid_at, stripe_subscription_id")
        .eq("user_id", userId)
        .maybeSingle(),
      supabaseAdmin
        .from("paypal_subscriptions")
        .select("paypal_subscription_id, status")
        .eq("user_id", userId)
        .in("status", ["APPROVED", "ACTIVE", "SUSPENDED"])
        .maybeSingle(),
    ]);

    if (
      stripeMem.data?.status === "active" ||
      stripeMem.data?.paid_at ||
      stripeMem.data?.stripe_subscription_id
    ) {
      return { ok: false as const, reason: "already_active_stripe" as const, message: "You already have an active Stripe membership." };
    }
    if (existingPaypal.data) {
      return {
        ok: false as const,
        reason: "already_pending_paypal" as const,
        message: "You already have a PayPal subscription in progress.",
        subscription_id: existingPaypal.data.paypal_subscription_id,
      };
    }

    // Local APPROVAL_PENDING rows may actually be APPROVED/ACTIVE on PayPal
    // if the user completed approval but the webhook never landed. Reconcile
    // each one against PayPal live before deciding it's abandoned — otherwise
    // we can wrongly cancel a real, paid subscription locally and block the
    // customer from being marked as a paid Member.
    const { data: pendingRows } = await supabaseAdmin
      .from("paypal_subscriptions")
      .select("paypal_subscription_id")
      .eq("user_id", userId)
      .eq("status", "APPROVAL_PENDING");

    if (pendingRows && pendingRows.length > 0) {
      const { applyPaypalSubscriptionState } = await import("./paypal-activation.server");
      for (const row of pendingRows) {
        try {
          const live = await getPaypalSubscription(row.paypal_subscription_id);
          if (
            live.status === "APPROVED" ||
            live.status === "ACTIVE" ||
            live.status === "SUSPENDED"
          ) {
            // Mirror live state (activates membership if initial payment
            // is completed) and block this new attempt.
            await applyPaypalSubscriptionState(supabaseAdmin, live, {
              source: "return_sync",
              eventId: `create-attempt-reconcile:${Date.now()}`,
            });
            return {
              ok: false as const,
              reason: "already_pending_paypal" as const,
              message:
                "You already have a PayPal subscription in progress. We've re-synced its status — please refresh your membership page.",
              subscription_id: row.paypal_subscription_id,
            };
          }
          if (live.status === "APPROVAL_PENDING") {
            // Genuinely still awaiting approval on PayPal side. Leave the
            // row as-is so the user can resume it or let PayPal expire it.
            continue;
          }
          // CANCELLED / EXPIRED on PayPal side — safe to close locally.
          await supabaseAdmin
            .from("paypal_subscriptions")
            .update({ status: live.status ?? "CANCELLED" })
            .eq("paypal_subscription_id", row.paypal_subscription_id);
        } catch (err) {
          console.warn(
            "[paypal] reconcile of APPROVAL_PENDING row failed; leaving as-is",
            { subscriptionId: row.paypal_subscription_id, err: err instanceof Error ? err.message : err },
          );
        }
      }
    }




    // Look up product + plan from paypal_config (admin-managed), for the
    // membership plan the customer chose before creating their account.
    const { resolvePaypalPlan } = await import("./paypal-plan.server");
    const resolved = await resolvePaypalPlan(supabaseAdmin, data.plan_id);
    if (!resolved) {
      return {
        ok: false as const,
        reason: "not_configured" as const,
        message: "PayPal isn't available for this membership option yet.",
      };
    }
    const { productId, planId: standardPlanId, membershipPlan } = resolved;
    const voucherOutcome = await applyPaypalVoucher(supabaseAdmin, {
      code: data.voucher_code,
      userId,
      email,
      membershipPlanId: membershipPlan.id,
      productId,
      fallbackPlanId: standardPlanId,
    });
    const planId = voucherOutcome.planId;


    const startTimeIso = computePaypalStartTime();

    let sub: PaypalSubscription;
    try {
      sub = await createPaypalSubscription({
        planId,
        customId: userId,
        startTimeIso,
        returnUrl: data.return_url,
        cancelUrl: data.cancel_url,
        requestId: `rt-${userId}-${Date.now()}`,
      });
    } catch (e) {
      return {
        ok: false as const,
        reason: "paypal_error" as const,
        message: e instanceof Error ? e.message.slice(0, 400) : "PayPal request failed.",
      };
    }

    // Store as pending. Never activate access here — the webhook does that.
    await supabaseAdmin.from("paypal_subscriptions").insert({
      user_id: userId,
      paypal_subscription_id: sub.id,
      product_id: productId,
      plan_id: planId,
      status: sub.status ?? "APPROVAL_PENDING",
      start_time: startTimeIso,
      setup_fee_status: "not_applicable",
      setup_fee_amount_cents: 0,

    });

    await holdPaypalVoucher(supabaseAdmin, voucherOutcome.resolved, {
      userId,
      email,
      subscriptionId: sub.id,
    });

    const approvalUrl =
      sub.links?.find((l) => l.rel === "approve")?.href ?? null;

    await recordDigitalConsent({
      userId,
      email,
      planId: data.plan_id ?? null,
      provider: "paypal",
      providerReference: sub.id,
    });

    await recordCheckoutAttribution({
      attribution: data.attribution,
      provider: "paypal",
      flow: "member_checkout",
      userId,
      email,
      planId: data.plan_id ?? null,
      checkoutRef: sub.id,
    });

    return {
      ok: true as const,
      subscription_id: sub.id,
      approval_url: approvalUrl,
      status: sub.status,
      start_time: startTimeIso,
    };
  });

// ---------- Read my subscription ----------

export const getMyPaypalSubscription = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z.object({ subscription_id: z.string().min(1).optional() }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    let q = supabase
      .from("paypal_subscriptions")
      .select(
        "paypal_subscription_id, status, plan_id, setup_fee_status, start_time, next_billing_time, paid_through, cancelled_at, suspended_at, created_at",
      )
      .eq("user_id", userId);
    if (data.subscription_id) q = q.eq("paypal_subscription_id", data.subscription_id);
    q = q.order("created_at", { ascending: false }).limit(1);
    const { data: row, error } = await q.maybeSingle();
    if (error) throw error;
    return row;
  });

/**
 * On-demand sync: called by the /paypal/return page so we don't have to wait
 * for the webhook. Fetches the subscription live from PayPal and applies its
 * status to our DB (including mirroring to memberships when ACTIVE). Safe to
 * call many times — idempotent via applyPaypalSubscriptionState.
 */
export const syncMyPaypalSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z.object({ subscription_id: z.string().min(1) }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Guard: only sync a subscription that belongs to the caller.
    const { data: owned } = await supabaseAdmin
      .from("paypal_subscriptions")
      .select("user_id")
      .eq("paypal_subscription_id", data.subscription_id)
      .maybeSingle();
    if (!owned || owned.user_id !== userId) {
      console.warn("[paypal-return-sync] not owned", { userId, sub: data.subscription_id });
      return { ok: false as const, reason: "not_found" as const };
    }
    let live: PaypalSubscription;
    try {
      live = await getPaypalSubscription(data.subscription_id);
    } catch (e) {
      console.error("[paypal-return-sync] live GET failed", {
        sub: data.subscription_id,
        err: e instanceof Error ? e.message : String(e),
      });
      return { ok: false as const, reason: "paypal_error" as const };
    }
    const { applyPaypalSubscriptionState } = await import("./paypal-activation.server");
    const result = await applyPaypalSubscriptionState(supabaseAdmin, live, {
      source: "return_sync",
    });
    console.info("[paypal-return-sync] applied", {
      sub: data.subscription_id,
      liveStatus: live.status,
      activated: result.activated,
    });
    return { ok: true as const, ...result };
  });

// ---------- Reactivate a cancelled PayPal membership ----------

/**
 * Which membership plan a restart would use for this member — the plan they
 * were last on. Used so a comeback code is priced against the plan PayPal
 * will actually bill, never a guess made in the browser.
 */
export const getMyReactivationPlanId = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("memberships")
      .select("plan_id")
      .eq("user_id", context.userId)
      .maybeSingle();
    const { getPlan } = await import("./membership-plans");
    return { plan_id: getPlan(data?.plan_id ?? null).id };
  });

/**
 * Start a brand-new PayPal subscription for a member whose previous PayPal
 * subscription is cancelled.
 *
 * PayPal cannot resume a cancelled subscription, so the only honest route is
 * a fresh checkout the member approves themselves. This function therefore
 * NEVER charges, never reactivates access and never writes a membership —
 * it only returns a PayPal approval URL. Access is granted later by the
 * webhook / return sync, exactly like a first-time signup.
 *
 * Duplicate protection: refuses outright if the member already has a live
 * subscription on PayPal (locally or live on PayPal's side) or an active
 * Stripe subscription.
 */
export const startMyPaypalReactivation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({
        return_url: z.string().url(),
        cancel_url: z.string().url(),
        /**
         * Optional comeback code (for example COMEBACK50). Re-validated here,
         * so a returning PayPal member gets the same welcome-back price as a
         * returning card member.
         */
        voucher_code: z.string().trim().max(60).nullish().transform((v) => v ?? undefined),
        digital_consent: digitalConsentSchema,
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    if (!data.digital_consent) {
      return { ok: false as const, reason: "consent_required" as const, message: MISSING_CONSENT_MESSAGE };
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [stripeMem, livePaypal, history] = await Promise.all([
      supabaseAdmin
        .from("memberships")
        .select("status, stripe_subscription_id, plan_id")
        .eq("user_id", userId)
        .maybeSingle(),
      supabaseAdmin
        .from("paypal_subscriptions")
        .select("paypal_subscription_id, status")
        .eq("user_id", userId)
        .in("status", ["APPROVAL_PENDING", "APPROVED", "ACTIVE", "SUSPENDED"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabaseAdmin
        .from("paypal_subscriptions")
        .select("paypal_subscription_id, plan_id, product_id, status, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (
      stripeMem.data?.stripe_subscription_id &&
      (stripeMem.data.status ?? "") === "active"
    ) {
      return {
        ok: false as const,
        reason: "already_active_stripe" as const,
        message: "You already have an active card membership, so there is nothing to restart.",
      };
    }

    if (livePaypal.data) {
      return {
        ok: false as const,
        reason: "already_live_paypal" as const,
        message:
          "Your PayPal membership is already running, so there is nothing to restart.",
        subscription_id: livePaypal.data.paypal_subscription_id,
      };
    }

    // Belt and braces: the most recent row may be stale locally. Ask PayPal
    // before creating a second subscription for the same person.
    const previous = history.data;
    if (previous?.paypal_subscription_id) {
      try {
        const live = await getPaypalSubscription(previous.paypal_subscription_id);
        if (live.status === "ACTIVE" || live.status === "APPROVED" || live.status === "SUSPENDED") {
          const { applyPaypalSubscriptionState } = await import("./paypal-activation.server");
          await applyPaypalSubscriptionState(supabaseAdmin, live, {
            source: "return_sync",
            eventId: `reactivate-guard:${Date.now()}`,
          });
          return {
            ok: false as const,
            reason: "already_live_paypal" as const,
            message:
              "Your PayPal membership is still active — we have re-synced it, so no new subscription is needed.",
            subscription_id: previous.paypal_subscription_id,
          };
        }
      } catch (err) {
        console.warn("[paypal-reactivate] live status check failed", {
          subscriptionId: previous.paypal_subscription_id,
          err: err instanceof Error ? err.message : err,
        });
      }
    }

    // Reuse the plan the member was on before, so the price they approve is
    // the price they know. Fall back to the plan on their membership record.
    let productId = previous?.product_id ?? null;
    let planId = previous?.plan_id ?? null;
    const { resolvePaypalPlan } = await import("./paypal-plan.server");
    const catalogue = await resolvePaypalPlan(supabaseAdmin, stripeMem.data?.plan_id);
    if (!productId || !planId) {
      if (!catalogue) {
        return {
          ok: false as const,
          reason: "not_configured" as const,
          message: "We could not find your previous PayPal plan. Please email info@recoverytrauma.com.",
        };
      }
      productId = catalogue.productId;
      planId = catalogue.planId;
    }

    // Comeback code: swap in the matching discounted PayPal plan so the
    // member approves the reduced price inside PayPal itself.
    let voucherOutcome: { planId: string; resolved: unknown | null } = {
      planId,
      resolved: null,
    };
    if (data.voucher_code && catalogue) {
      voucherOutcome = await applyPaypalVoucher(supabaseAdmin, {
        code: data.voucher_code,
        userId,
        email: null,
        membershipPlanId: catalogue.membershipPlan.id,
        productId,
        fallbackPlanId: planId,
      });
      planId = voucherOutcome.planId;
    }


    let sub: PaypalSubscription;
    try {
      sub = await createPaypalSubscription({
        planId,
        customId: userId,
        startTimeIso: null,
        returnUrl: data.return_url,
        cancelUrl: data.cancel_url,
        requestId: `rt-reactivate-${userId}-${Date.now()}`,
      });
    } catch (e) {
      return {
        ok: false as const,
        reason: "paypal_error" as const,
        message: e instanceof Error ? e.message.slice(0, 400) : "PayPal request failed.",
      };
    }

    await supabaseAdmin.from("paypal_subscriptions").insert({
      user_id: userId,
      paypal_subscription_id: sub.id,
      product_id: productId,
      plan_id: planId,
      status: sub.status ?? "APPROVAL_PENDING",
      start_time: null,
      setup_fee_status: "not_applicable",
      setup_fee_amount_cents: 0,
    });

    await holdPaypalVoucher(supabaseAdmin, voucherOutcome.resolved, {
      userId,
      email: null,
      subscriptionId: sub.id,
    });

    await recordDigitalConsent({
      userId,
      email: (context.claims as { email?: string } | undefined)?.email ?? null,
      planId: null,
      provider: "paypal",
      providerReference: sub.id,
    });

    return {
      ok: true as const,
      subscription_id: sub.id,
      approval_url: sub.links?.find((l) => l.rel === "approve")?.href ?? null,
      status: sub.status,
    };
  });


// ---------- Admin: set webhook_id / toggle public flag ----------

async function assertAdminContext(context: unknown) {
  const ctx = context as {
    supabase: {
      from: (t: string) => {
        select: (c: string) => {
          eq: (k: string, v: string) => {
            eq: (k2: string, v2: string) => {
              maybeSingle: () => Promise<{ data: unknown }>;
            };
          };
        };
      };
    };
    userId: string;
  };
  const { data } = await ctx.supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", ctx.userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Forbidden");
}

export const saveWebhookId = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ webhook_id: z.string().min(6) }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdminContext(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("paypal_config").upsert(
      {
        key: "webhook",
        value: { id: data.webhook_id, updated_at: new Date().toISOString() },
      },
      { onConflict: "key" },
    );
    return { ok: true };
  });

export const getPaypalAdminOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdminContext(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: cfg }, { data: subs }, { data: flag }] = await Promise.all([
      supabaseAdmin.from("paypal_config").select("key, value"),
      supabaseAdmin
        .from("paypal_subscriptions")
        .select(
          "paypal_subscription_id, user_id, status, setup_fee_status, start_time, next_billing_time, paid_through, cancelled_at, created_at, paypal_payer_email",
        )
        .order("created_at", { ascending: false })
        .limit(50),
      supabaseAdmin
        .from("feature_flags")
        .select("key, enabled")
        .eq("key", "paypal_public_checkout_enabled")
        .maybeSingle(),
    ]);
    return {
      config: cfg ?? [],
      subscriptions: subs ?? [],
      public_enabled: !!flag?.enabled,
    };
  });

export const setPaypalPublicEnabled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ enabled: z.boolean() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdminContext(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("feature_flags")
      .update({ enabled: data.enabled, enabled_for_admins_only: !data.enabled })
      .eq("key", "paypal_public_checkout_enabled");
    return { ok: true };
  });

export const adminLookupPaypalSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ subscription_id: z.string().min(6) }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdminContext(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: row }, live] = await Promise.all([
      supabaseAdmin
        .from("paypal_subscriptions")
        .select("*")
        .eq("paypal_subscription_id", data.subscription_id)
        .maybeSingle(),
      getPaypalSubscription(data.subscription_id).catch((e) => ({
        error: e instanceof Error ? e.message : String(e),
      })),
    ]);
    return { local: row, live };
  });
